//@ts-nocheck
import { updateDOMContent } from './dom-utils.js';
import { vaultImgs } from './data.js';
import { getSelectedShopifyOptions, selectOptionInput } from './form-utils.js';
import { debounce, throttle } from './utils.js';
import { updateGallery } from './gallery-utils.js';
import { setupExpandableContent } from './setupExpandableContent.js';
import { getPlainText } from './getPlainText.js';
import {
  getScriptContent,
  parseJsonOrNull,
  parseShopifyOptions,
  findShopifyVariant,
  resolveInitialVariant,
} from './product-data.js';
import { setupProductDescriptionToggle } from './ProductDescriptionToggle.js';

class ProductPreviewHandler {
  static swiper_slide_class =
    'swiper-slide place-content-center will-change-contents flex rounded-3xl';
  static swiper_slide_img =
    'mx-auto self-center w-[133px] h-[200px] md:w-[200px] md:h-[300px] will-change-contents justify-self-center rounded-3xl';

  constructor(sectionElement) {
    this.sectionElement = sectionElement;
    this.productForm = null;
    this.shopifyProductData = null;
    this.allVaultVariantData = null;
    this.uiGeneratedOptions = null;
    this.variantIdInput = null;
    this.swiperInstance = null;
    this.onOptionChangedDebounced = null;
    this.onResizeThrottled = null;
    this.initializeSwiper = null;

    if (!this.sectionElement) return;

    if (!this.loadInitialData() || !this.setupFormElements()) {
      return;
    }

    this.uiGeneratedOptions = parseShopifyOptions(this.shopifyProductData);
    this.init();
  }

  getSwiperConfig() {
    return {
      slidesPerView: 1,
      initialSlide: 0,
      direction: 'horizontal',
      freeMode: true,
      loop: true,
      grabCursor: false,
      speed: 400,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      on: {
        init: () => console.log('[Swiper] Yes'),
      },
    };
  }

  loadInitialData() {
    const rawProductJson =
      getScriptContent(this.sectionElement, 'script[data-product-json]') ||
      this.sectionElement?.dataset?.productJson?.trim();

    const productData = parseJsonOrNull(rawProductJson);
    const rawVaultJson = getScriptContent(
      this.sectionElement,
      'script[data-all-vault-variants-json]'
    );
    const vaultData = parseJsonOrNull(rawVaultJson);

    const isValidProductData =
      productData &&
      typeof productData === 'object' &&
      Array.isArray(productData.variants) &&
      productData.variants.length > 0;

    if (isValidProductData && vaultData) {
      this.shopifyProductData = productData;
      this.allVaultVariantData = vaultData;
      return true;
    }

    this.shopifyProductData = null;
    this.allVaultVariantData = null;
    return false;
  }

  setupFormElements() {
    const productFormId = this.sectionElement.dataset.productFormId;
    if (!productFormId) return false;

    this.productForm = this.sectionElement.querySelector(`#${productFormId}`);
    if (!this.productForm) return false;

    this.variantIdInput = this.productForm.querySelector('[data-variant-id-input]');
    return true;
  }

  getSelectedOptions() {
    return getSelectedShopifyOptions(
      this.productForm,
      this.uiGeneratedOptions,
      this.shopifyProductData?.variants?.[0]
    );
  }

  getMatchingVariant(selectedUIOpts) {
    return findShopifyVariant(
      this.shopifyProductData?.variants,
      this.uiGeneratedOptions,
      selectedUIOpts
    );
  }

  updateImageGallery() {
    const wrapper = this.sectionElement.querySelector('[data-swiper-wrapper]');
    if (!wrapper) return;

    const mediaItems = this.resolveVariantMedia();

    window.requestAnimationFrame(() => {
      this.swiperInstance = updateGallery(
        wrapper,
        this.swiperInstance,
        mediaItems,
        ProductPreviewHandler.swiper_slide_class,
        ProductPreviewHandler.swiper_slide_img,
        this.shopifyProductData.title,
        this.getSwiperConfig()
      );

      document.dispatchEvent(new CustomEvent('variantimages:updated', { bubbles: true }));
    });
  }

  resolveVariantMedia() {
    const variant = this.getMatchingVariant(this.getSelectedOptions());
    if (!variant) return [];

    const selectedColor = variant.option1?.toLowerCase();
    const colorMap = {
      comfy: 'comfyrobe',
      fjellblå: 'fjellbla',
      vargnatt: 'vargnatt',
      fjellhimmel: 'specialEdition',
    };
    let colorKey = colorMap[selectedColor] || selectedColor;

    if (!vaultImgs[colorKey]) {
      const productHandle = this.shopifyProductData.handle?.toLowerCase();
      if (productHandle?.includes('comfy')) {
        colorKey = 'comfyrobe';
      } else if (productHandle?.includes('specialEdition')) {
        colorKey = 'fjellhimmel';
      }
    }

    return (vaultImgs[colorKey] || []).map((url) => ({
      src: url,
      alt: `${this.shopifyProductData.title} - ${colorKey}`,
    }));
  }

  updateUI(shopifyVariant) {
    const targetShopifyVariant =
      shopifyVariant ||
      this.shopifyProductData.selected_or_first_available_variant ||
      this.shopifyProductData.variants?.[0];

    const vaultVariantData = targetShopifyVariant
      ? this.allVaultVariantData[targetShopifyVariant.id]
      : null;

    if (targetShopifyVariant && vaultVariantData) {
      if (this.variantIdInput) {
        this.variantIdInput.value = targetShopifyVariant.id;
      }
      this.updateImageGallery();
      updateDOMContent(
        this.sectionElement,
        '[data-variant-subtitle]',
        vaultVariantData.subtitle?.value,
        true
      );

      const descriptionContainer = this.sectionElement.querySelector(
        '[data-description-toggle-container]'
      );
      if (!descriptionContainer) return;

      const contentEl = descriptionContainer.querySelector('.metafield-content');
      if (!contentEl) return;

      // HENT HTML-STRENGEN FRA DATA-ATTRIBUTTET
      const descriptionHTML = contentEl.dataset.fullText || '';
      const plainText = getPlainText(descriptionHTML);

      const previewEl = descriptionContainer.querySelector('[data-description-preview]');
      const toggleButton = descriptionContainer.querySelector('.metafield-toggle-button');

      if (!previewEl || !toggleButton) {
        descriptionContainer.style.display = 'none';
        return;
      }

      if (!plainText) {
        descriptionContainer.style.display = 'none';
        return;
      }

      descriptionContainer.style.display = 'block';

      if (plainText.length < 69) {
        previewEl.innerHTML = descriptionHTML;
        previewEl.style.display = 'block';
        contentEl.style.display = 'none';
        toggleButton.style.display = 'none';
      } else {
        const previewText = plainText.slice(0, 69).trim() + '...';
        previewEl.textContent = previewText;
        previewEl.style.display = 'block';
        toggleButton.style.display = 'inline-flex';

        setupProductDescriptionToggle(descriptionContainer);
      }
    } else {
      this.updateImageGallery();
      updateDOMContent(
        this.sectionElement,
        '[data-variant-subtitle]',
        'Produktinformasjon utilgjengelig',
        true
      );
      const descriptionContainer = this.sectionElement.querySelector(
        '[data-description-toggle-container]'
      );
      if (descriptionContainer) {
        descriptionContainer.style.display = 'none';
      }
    }
  }

  onOptionChanged() {
    const selectedOpts = this.getSelectedOptions();
    const shopifyVariant = this.getMatchingVariant(selectedOpts);

    if (shopifyVariant) {
      this.variantIdInput && (this.variantIdInput.value = shopifyVariant.id);

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('variant', String(shopifyVariant.id));
      window.history.replaceState({ path: currentUrl.href }, '', currentUrl.href);
    }

    this.updateUI(shopifyVariant);
  }

  initializeOptions() {
    if (!this.hasVariants()) {
      this.onOptionChangedDebounced();
      return;
    }

    const initialVariant = resolveInitialVariant(this.shopifyProductData);
    if (!initialVariant) {
      this.onOptionChangedDebounced();
      return;
    }
    this.uiGeneratedOptions.forEach(({ name, position }) => {
      if (!name || typeof position !== 'number') return;
      const value = initialVariant[`option${position}`];
      if (value != null) selectOptionInput(this.productForm, name, value);
    });

    this.onOptionChangedDebounced();
  }

  hasVariants() {
    return (
      Array.isArray(this.shopifyProductData?.variants) &&
      this.shopifyProductData.variants.length > 0
    );
  }

  init() {
    this.onOptionChangedDebounced = debounce(() => this.onOptionChanged(), 200);
    this.onResizeThrottled = throttle(() => {
      if (this.swiperInstance) {
        this.swiperInstance.update();
      } else {
        this.updateImageGallery();
      }
    }, 250);

    this.productForm
      .querySelectorAll('.product-option-input')
      .forEach((input) => input.addEventListener('change', this.onOptionChangedDebounced));

    window.addEventListener('resize', this.onResizeThrottled);

    ['.color-option-item', '.size-option-item'].forEach((selector) => {
      document.querySelectorAll(selector).forEach((item) => {
        item.addEventListener('click', () => {
          const input = item.querySelector('input[type="radio"]');
          if (input) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    });

    this.initializeOptions();

    setupExpandableContent(this.sectionElement, '.expandable-metafield', '.metafield-content');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product-preview-section[data-section-id]').forEach((sectionNode) => {
    if (
      sectionNode instanceof HTMLElement &&
      (sectionNode.dataset.productJson ||
        sectionNode.querySelector('script[data-product-json-script]'))
    ) {
      try {
        new ProductPreviewHandler(sectionNode);
      } catch (e) {
        console.error(
          `Error initializing ProductPreviewHandler for section ${sectionNode.dataset.sectionId}:`,
          e
        );
      }
    }
  });
});
